/**
 * Browser APM facade over the New Relic Browser agent or Datadog RUM.
 *
 * Django picks the provider from env vars and injects that agent's snippet
 * (see `apm_browser_header` in portal/templatetags/global_functions.py), so
 * whichever global exists at call time is the one to report to.
 */

interface DatadogRumEvent {
    type: string;
    error?: { message?: string };
}

interface DatadogRum {
    onReady(callback: () => void): void;
    addError(error: unknown, context?: object): void;
}

declare global {
    interface Window {
        NREUM: typeof newrelic | undefined;
        DD_RUM: DatadogRum | undefined;
        /** Called from the `beforeSend` set up by the Datadog snippet; false drops the event */
        lystRumBeforeSend?: (event: DatadogRumEvent, context?: { error?: unknown }) => boolean;
    }
}

export type BrowserApmProvider = "datadog" | "newrelic" | "none";

/** Receives an Error, or whatever else was thrown; return true to discard it */
export type ErrorFilter = (error: any) => boolean;

export function getProvider(): BrowserApmProvider {
    if (window.DD_RUM) {
        return "datadog";
    }
    if (window.NREUM) {
        return "newrelic";
    }
    return "none";
}

export function noticeError(error: Error, data?: Record<string, any>): void {
    if (window.DD_RUM) {
        const rum = window.DD_RUM;
        rum.onReady(() => rum.addError(error, data));
    } else if (window.NREUM) {
        (window.NREUM.noticeError as any)(error, data);
    }
}

export function setErrorFilter(shouldDiscard: ErrorFilter): void {
    if (window.DD_RUM) {
        window.lystRumBeforeSend = (event, context) =>
            event.type !== "error" || !shouldDiscard(context?.error ?? event.error?.message);
    } else if (window.NREUM) {
        window.NREUM.setErrorHandler(shouldDiscard);
    }
}
